<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            class="first-income-detail"
            ref="first_grid"
            :autoGroupColumnDef="autoGroupColumnDef"
            :getDataPath="getDataPath"
            :treeData="true"
            :column-defs="columnDefs"
            :row-data="firstRowData"
            :is-search="false"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :getRowStyle="getRowStyle"
            :pinned-bottom-row-data="firstPinnedBottomRowData"
            grid-theme="ag-theme-alpine-report"
        >
            <responsive-button
                class="d-none d-md-flex"
                :clickable="false"
                :responsiveness="false"
                variant="link"
                >Period #1</responsive-button
            >
            <div class="d-flex align-items-baseline mr-1">
                <datepicker
                    variant="primary"
                    label-help="Recorded by 1"
                    :value="firstRecordedBy"
                    :display-format="userUnicodeDateFormat"
                    :responsiveness="isMobile"
                    @input="handleFirstRecordedByChange"
                />
            </div>
            <date-range-picker
                :range-options="predefinedRanges"
                :value="firstDateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleFirstDaterangeChange"
            />
            <div class="d-none d-lg-flex">
                <export-to-excel-button
                    :href="exportExcelHref"
                ></export-to-excel-button>
                <exportToPDFButton
                    :href="exportToPdfHref"
                    :pdfCacheKey="pdfCacheKey"
                />
            </div>
            <b-button-group class="mr-1">
                <more-filters-dropdown class="comparative-dropdown">
                        <form-square-switch
                            v-for="filter in comparativeFilters"
                            :key="filter.key"
                            :labels="{
                                checked: filter.labels,
                                unchecked: filter.labels,
                            }"
                            v-model="filterValues[filter.key]"
                            class="mb-1 comparative-toggle"
                            :sync="true"
                        />
                        <export-to-excel-button
                            class="d-lg-none d-flex"
                            :responsiveness="false"
                            :display-label="true"
                            :href="exportExcelHref"
                        ></export-to-excel-button>
                        <exportToPDFButton
                            :href="exportToPdfHref"
                            :pdfCacheKey="pdfCacheKey"
                            class="d-lg-none d-flex"
                            :responsiveness="false"
                            :display-label="true"
                        />

                </more-filters-dropdown>
            </b-button-group>
            <responsive-button
                variant="outline-primary"
                @click="generate"
                :responsiveness="false"
                >Generate</responsive-button
            >
           
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
        <grid
            class="h-100 second-income-detail"
            ref="second_grid"
            :autoGroupColumnDef="autoGroupColumnDef"
            :getDataPath="getDataPath"
            :treeData="true"
            :columnDefs="columnDefs"
            :rowData="secondRowData"
            :isSearch="false"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :statusBar="statusBar"
            :getRowStyle="getRowStyle"
            :pinned-bottom-row-data="secondPinnedBottomRowData"
            gridTheme="ag-theme-alpine-report"
        >
            <responsive-button
                class="d-none d-md-flex"
                :clickable="false"
                :responsiveness="false"
                variant="link"
                >Period #2</responsive-button
            >
            <div class="d-flex align-items-baseline mr-1">
                <datepicker
                    variant="primary"
                    label-help="Recorded by 2"
                    :value="secondRecordedBy"
                    :display-format="userUnicodeDateFormat"
                    :responsiveness="isMobile"
                    @input="handleSecondRecordedByChange"
                />
            </div>
            <date-range-picker
                :range-options="predefinedRanges"
                :value="secondDateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleSecondDaterangeChange"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import Modal from '../generic/modal/modal.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import Datepicker from '../generic/datepicker/datepicker.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    ALL_DATES,
    TOMORROW,
    THIS_WEEK,
    NEXT_WEEK,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_YEAR,
    LAST_WEEK,
} from '../generic/date-range-picker/date-range-picker.vue';
import {
    getPinnedBottomRowData,
} from './income-detail/helpers';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    DATE_PICKER_INPUT,
    SWITCH_INPUT,
} from './report-settings/report-settings-modal.vue';
import { REPORT_COMPARATIVE_ENDPOINT } from '../../endpoints';
import ReportSummaryBar from './report-summary-bar.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { isMediumScreen } from '../../utils/screens';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    name: 'ComparativeReport',
    components: {
        exportToPDFButton,
        Page,
        Grid,
        Modal,
        Datepicker,
        ResponsiveButton,
        ResponsiveDropdown,
        DateRangePicker,
        reportSummaryBar: ReportSummaryBar,
        FormSquareSwitch,
        ExportToExcelButton,
        MoreFiltersDropdown,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Comparative',
            tabs: [
                {
                    title: 'Comparative',
                    route: '/app/reports/comparative/',
                    active: true,
                },
            ],
            firstRecordedBy: format(new Date(), 'yyyy-MM-dd'),
            firstDateRange: nameToRange(THIS_YEAR),
            secondDateRange: nameToRange(LAST_YEAR),
            secondRecordedBy: format(
                new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
                'yyyy-MM-dd'
            ),
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            getRowStyle: null,
            firstColumnApi: null,
            firstGridApi: null,
            firstRowData: [],
            firstAverage: [],
            firstTotals: [],
            secondColumnApi: null,
            secondGridApi: null,
            secondRowData: [],
            secondAverage: [],
            secondTotals: [],
            filterValues: {
                showRooms: false,
                showDisabledProperties: false,
                showPropertiesWithNoActivity: true,
            },
            windowWidth: 0,
            windowHeight: 0,
            statusBar: null,
            getDataPath: (data) => data.id_hierarchy,
            autoGroupColumnDef: {
                flex: 1,
                cellRendererParams: {
                    innerRenderer: '',
                },
                pdfExportOptions: {
                    skipColumn: true,
                },
                minWidth: 40,
                maxWidth: 40,
                resizable: false,
                headerName: '',
                pinned: 'left',
            },
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    field: 'rental_property.name',
                    headerName: 'Property/Room',
                    valueGetter: ({ data, node }) => {
                        if (!data) {
                            return '';
                        }
                        return node.rowPinned
                            ? data.name
                            : data.room
                            ? data.room.name
                            : data.rental_property && data.rental_property.name;
                    },
                    cellStyle: ({ data, node }) => {
                        if (!data) {
                            return null;
                        }
                        return node.rowPinned || !data.room
                            ? { 'font-weight': 'bold' }
                            : null;
                    },
                    width: isMediumScreen() ? undefined : 80,
                    autoWidthExclude: this.isMobile,
                    pinned: 'left',
                },
                {
                    field: 'available_nts',
                    headerName: 'Nts Avail',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
                {
                    field: 'booked_nts',
                    headerName: 'Nts Booked',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
                {
                    field: 'blocked_nts',
                    headerName: 'Nts Blocked',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
                {
                    field: 'occupied_nts',
                    headerName: 'Nts Occupied',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
                {
                    field: 'booked_percent',
                    headerName: 'Guest Occ %',
                    type: 'amountColumn',
                    cellRendererParams: {
                        percentage: true,
                    },
                    minWidth: 100,
                },
                {
                    field: 'occupied_percent',
                    headerName: 'Total Occ %',
                    type: 'amountColumn',
                    cellRendererParams: {
                        percentage: true,
                    },
                    minWidth: 95,
                },
                {
                    field: 'rental_charges',
                    headerName: 'Rental Charges',
                    type: 'amountColumn',
                },
                {
                    field: 'avg_rental_charges',
                    headerName: 'Avg Per Day',
                    type: 'amountColumn',
                    minWidth: 95,
                },
                {
                    field: 'fee_service_revenue',
                    headerName: 'Fee/Service Revenue',
                    type: 'amountColumn',
                    minWidth: 95,
                },
                {
                    field: 'avg_fee_service_revenue',
                    headerName: 'Avg Per Day',
                    type: 'amountColumn',
                    minWidth: 95,
                },
                {
                    field: 'tax',
                    headerName: 'Tax',
                    type: 'amountColumn',
                },
                {
                    field: 'total_revenues',
                    headerName: 'Total Revenues',
                    type: 'amountColumn',
                },
                {
                    field: '',
                    headerName: '',
                    autoWidthExclude: true,
                    pdfExportOptions: {
                        skipColumn: true,
                    },
                    sortable: false,
                },
            ];
        },
        firstPinnedBottomRowData() {
            return getPinnedBottomRowData(this.firstAverage, this.firstTotals);
        },
        secondPinnedBottomRowData() {
            return getPinnedBottomRowData(
                this.secondAverage,
                this.secondTotals
            );
        },
        exportToPdfHref() {
            let url = '/app/reports/comparative/row-data/pdf/';
            return url;
        },
        exportExcelHref() {
            let url = '/app/reports/comparative/row-data/excel/';
            url += `?from_date_1=${this.firstStartDateRequest}`;
            url += `&to_date_1=${this.firstEndDateRequest}`;
            url += `&recorded_by_date_1=${this.firstRecordedBy}`;
            url += `&from_date_2=${this.secondStartDateRequest}`;
            url += `&to_date_2=${this.secondEndDateRequest}`;
            url += `&recorded_by_date_2=${this.secondRecordedBy}`;
            url += `&show_rooms=${this.filterValues.showRooms}`;
            url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
            url += `&show_properties_with_no_activity=${this.filterValues.showPropertiesWithNoActivity}`;
            return url;
        },
        firstStartDateRequest() {
            return format(this.firstDateRange.from, this.dateRequestFormat);
        },
        firstEndDateRequest() {
            return format(this.firstDateRange.to, this.dateRequestFormat);
        },
        secondStartDateRequest() {
            return format(this.secondDateRange.from, this.dateRequestFormat);
        },
        secondEndDateRequest() {
            return format(this.secondDateRange.to, this.dateRequestFormat);
        },
        comparativeFilters() {
            return [
                { labels: 'Show Rooms', key: `showRooms` },
                {
                    labels: 'Show Disabled Properties',
                    key: `showDisabledProperties`,
                },
                {
                    labels: 'Show Properties with No Activity',
                    key: `showPropertiesWithNoActivity`,
                },
            ];
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
        reportSettings() {
            return {
                name: 'Comparative',
                report_sysname: 'COMPARATIVE',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_year',
                        label: 'Date Period 1',
                        name: 'from_date_1',
                        predefinedRanges: this.predefinedRanges
                    },
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'last_year',
                        label: 'Date Period 2',
                        name: 'from_date_2',
                        predefinedRanges: this.predefinedRanges
                    },
                    {
                        type: DATE_PICKER_INPUT,
                        value: format(new Date(), 'yyyy-MM-dd'),
                        label: 'Recorded By Date 1',
                        name: 'recorded_by_date_1',
                    },
                    {
                        type: DATE_PICKER_INPUT,
                        value: format(
                            new Date(
                                new Date().setFullYear(
                                    new Date().getFullYear() - 1
                                )
                            ),
                            'yyyy-MM-dd'
                        ),
                        label: 'Recorded By Date 2',
                        name: 'recorded_by_date_2',
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        label: 'Show Rooms',
                        name: 'show_rooms',
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        label: 'Show Disabled Properties',
                        name: 'show_disabled_properties',
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        label: 'Show Properties With NO Activity',
                        name: 'show_properties_with_no_activity',
                    },
                ],
            };
        },
    },
    mounted() {
        const firstGrid = this.$refs.first_grid;
        firstGrid.gridOptions.suppressNoRowsOverlay = true;
        this.firstColumnApi = firstGrid.gridOptions.columnApi;
        this.firstGridApi = firstGrid.gridOptions.api;
        const secondGrid = this.$refs.second_grid;
        secondGrid.gridOptions.suppressNoRowsOverlay = true;
        this.secondColumnApi = secondGrid.gridOptions.columnApi;
        this.secondGridApi = secondGrid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.getRowStyle = ({ node }) => {
            if (node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        generate() {
            this.fetchData();
        },
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(REPORT_COMPARATIVE_ENDPOINT(), {
                    params: {
                        from_date_1: this.firstStartDateRequest,
                        to_date_1: this.firstEndDateRequest,
                        recorded_by_date_1: this.firstRecordedBy,
                        from_date_2: this.secondStartDateRequest,
                        to_date_2: this.secondEndDateRequest,
                        recorded_by_date_2: this.secondRecordedBy,
                        show_rooms: this.filterValues.showRooms,
                        show_disabled_properties:
                            this.filterValues.showDisabledProperties,
                        show_properties_with_no_activity:
                            this.filterValues.showPropertiesWithNoActivity,
                    },
                });
                const { first_data, second_data, pdf_cache_key } = data;
                this.pdfCacheKey = pdf_cache_key;
                this.firstRowData = first_data.properties;
                this.firstAverage = first_data.average;
                this.firstTotals = first_data.totals;
                this.secondRowData = second_data.properties;
                this.secondAverage = second_data.average;
                this.secondTotals = second_data.totals;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent =
                this.secondGridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
            componentInstance.setCaption(
                'Invoices w/ depart dates outside of reporting period will be prorated by night.'
            );
        },
        handleFirstDaterangeChange(value) {
            this.firstDateRange = { ...value };
        },
        handleSecondDaterangeChange(value) {
            this.secondDateRange = { ...value };
        },
        handleFirstRecordedByChange(value) {
            this.firstRecordedBy = value;
        },
        handleSecondRecordedByChange(value) {
            this.secondRecordedBy = value;
        },
    },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.comparative-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }

    .comparative-toggle {
        width: 245px;
        @include media-breakpoint-down(lg) {
            width: 270px;
        }
    }
}

.first-income-detail {
    display: inline;
}

.second-income-detail {
    display: inline;
    padding-left: 1px;
}
</style>
